import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {PresenterProps, withPresenter} from '../../Support/with_presenter';
import {DashboardPresenter} from './DashboardPresenter';
import {WorkoutCard} from './Components/WorkoutCard';
import {Loader} from '../Components/Utils/Loader';
import {WorkoutList} from '../Components/Workout/WorkoutList';
import {NetworkRequestStatusView} from '../Components/Utils/NetworkRequestStatusView';
import {PartialWorkout} from '../../Models/PartialWorkout';
import {QuickWorkoutLinks} from './Components/QuickWorkoutLinks';
import {LeaderboardSuggestions} from './Components/LeaderboardSuggestions';
import {LeaderboardSuggestion} from '../../Models/LeaderboardSuggestion';
import {PartialWorkoutsSummary} from '../Components/Workout/PartialWorkoutsSummary';
import {WorkoutCardViewModel} from './Components/WorkoutCardPresenter';
import {Link} from 'react-router-dom';
import {Feedback} from '../Components/Feedback';
import {CrewInvites} from '../Components/Crew/CrewInvites';
import {UpcomingEvents} from '../Components/Crew/UpcomingEvents';
import {ErrorPlaceholder} from '../Components/Utils/ErrorPlaceholder';
import {Report} from '../../Models/Report';
import {ReportCard} from '../Report/Index/Components/ReportCard';
import {FeatureFlags} from '../../FeatureFlags';

interface OwnProps {}

const DashboardComponent: FunctionComponent<OwnProps & PresenterProps<DashboardPresenter>> = observer(({presenter}) => {
    const reportsResult = presenter.reports;
    return (
        <div className="container">
            <div className="row">
                <div className="col-12">
                    <h1>My Activities</h1>
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-lg-9 order-1 order-lg-0">
                    <div className="row">
                        <div className="col-12 col-md-6">
                            <NetworkRequestStatusView<PartialWorkout[]>
                                result={presenter.recentWorkoutsResult}
                                loader={() => null}
                                error={() => null}
                                component={(data) => {
                                    return data.length > 0 ? (
                                        <WorkoutCard
                                            workoutCardViewModel={WorkoutCardViewModel.fromPartialWorkout(data[0])}
                                        />
                                    ) : null;
                                }}
                            />
                        </div>
                        <div className="col-12 col-md-6 d-none d-md-flex">
                            <NetworkRequestStatusView<PartialWorkout[]>
                                result={presenter.recentWorkoutsResult}
                                loader={() => null}
                                error={() => null}
                                component={(data) => {
                                    return data.length > 1 ? (
                                        <WorkoutCard
                                            workoutCardViewModel={WorkoutCardViewModel.fromPartialWorkout(data[1])}
                                        />
                                    ) : null;
                                }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <NetworkRequestStatusView<PartialWorkout[]>
                                result={presenter.allWorkoutsResult}
                                loader={() => <Loader className="mt-5" />}
                                error={() => <ErrorPlaceholder large className="mt-5" />}
                                component={() => {
                                    return (
                                        <>
                                            <div className="row">
                                                <div className="col-12 d-flex gap-2 align-items-end">
                                                    <h3 className="mt-4 mb-0">
                                                        {presenter.numWorkouts === 1
                                                            ? '1 Workout'
                                                            : presenter.numWorkouts + ' Workouts'}
                                                    </h3>
                                                    <Link to={'/workouts'}>View all</Link>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-12">
                                                    <div className="card mt-2">
                                                        <WorkoutList
                                                            workoutListViewModels={presenter.allWorkoutListViewModels}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {!FeatureFlags.reports && FeatureFlags.isAdvancedSubscriptionsEnabled && (
                                                <>
                                                    <div className="row mb-3">
                                                        <div className="col-12 d-flex gap-2 align-items-end">
                                                            <h3 className="mt-4 mb-0">Reports</h3>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-12">
                                                            <div className="card">
                                                                <div className="card-body text-center">
                                                                    <p>
                                                                        Unlock deeper insights with Premium Reporting!
                                                                    </p>
                                                                    <p>
                                                                        With our advanced analysis tools, you can now
                                                                        compare workouts side by side — whether it’s
                                                                        sections from the same session or from different
                                                                        days of dropping your split. Track your
                                                                        progress, spot patterns, and fine-tune your
                                                                        technique like never before.
                                                                    </p>
                                                                    <p>
                                                                        <b>
                                                                            Level up your training — become an Advanced
                                                                            Member today!
                                                                        </b>
                                                                    </p>
                                                                    <a
                                                                        href="/account/subscriptions/checkout"
                                                                        className="btn btn-primary"
                                                                    >
                                                                        Become an Advanced Member
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                            {FeatureFlags.reports && (
                                                <NetworkRequestStatusView<Report[]>
                                                    result={reportsResult}
                                                    loader={() => <Loader className="mt-5" />}
                                                    error={() => <ErrorPlaceholder className="mt-5" />}
                                                    component={(reports) => {
                                                        return (
                                                            <>
                                                                <div className="row mb-3">
                                                                    <div className="col-12 d-flex gap-2 align-items-end">
                                                                        <h3 className="mt-4 mb-0">Reports</h3>
                                                                        <Link to={'/reports'}>View all</Link>
                                                                    </div>
                                                                </div>
                                                                <div className="row">
                                                                    {reports.length === 0 && (
                                                                        <div className="col-12">
                                                                            <div className="card">
                                                                                <div className="card-body text-center">
                                                                                    <p>No reports found</p>
                                                                                    <small>
                                                                                        Start by gathering snippets to
                                                                                        combine into dynamic reports.
                                                                                    </small>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {reports.map((report) => {
                                                                        return (
                                                                            <div className="col-12 col-md-6">
                                                                                <ReportCard
                                                                                    className="mb-3"
                                                                                    report={report}
                                                                                />
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </>
                                                        );
                                                    }}
                                                />
                                            )}
                                        </>
                                    );
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="col-12 col-lg-3 order-0 order-lg-1">
                    <CrewInvites className="mb-3" />
                    <UpcomingEvents viewAllButton className="mb-3" />
                    <QuickWorkoutLinks className="mb-3 d-none d-lg-flex" />
                    <NetworkRequestStatusView<PartialWorkout[]>
                        result={presenter.allWorkoutsResult}
                        loader={() => (
                            <PartialWorkoutsSummary
                                compact
                                className="skeleton mb-3 d-none d-lg-flex"
                                partialWorkouts={[]}
                            />
                        )}
                        error={() => (
                            <PartialWorkoutsSummary
                                compact
                                className="skeleton mb-3 d-none d-lg-flex"
                                partialWorkouts={[]}
                            />
                        )}
                        component={(data) => (
                            <PartialWorkoutsSummary compact className="mb-3 d-none d-lg-flex" partialWorkouts={data} />
                        )}
                    />
                    <NetworkRequestStatusView<LeaderboardSuggestion[]>
                        result={presenter.leaderboardSuggestionResult}
                        loader={() => (
                            <LeaderboardSuggestions
                                className="skeleton mb-3 d-none d-lg-flex"
                                leaderboardSuggestions={[]}
                            />
                        )}
                        error={() => (
                            <LeaderboardSuggestions
                                className="skeleton mb-3 d-none d-lg-flex"
                                leaderboardSuggestions={[]}
                            />
                        )}
                        component={(data) => (
                            <LeaderboardSuggestions className="mb-3 d-none d-lg-flex" leaderboardSuggestions={data} />
                        )}
                    />
                    <Feedback className="d-none d-lg-flex" />
                </div>
            </div>
        </div>
    );
});
export const Dashboard = withPresenter<DashboardPresenter, OwnProps>(
    (props, component) =>
        new DashboardPresenter(component.network.workouts, component.network.leaderboard, component.network.report),
    DashboardComponent,
);
